import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled, Theme } from '@mui/material/styles';

// ***** Adjust these values to control hero height
const HERO_HEIGHT = {
    DESKTOP: '480px',      // Increased to accommodate CTA
    TABLET: '300px',       // Medium screens
    MOBILE: '212px'        // Mobile screens (CTA below hero on mobile)
};

const HeroContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
    position: 'relative',
    width: '100%',
    height: HERO_HEIGHT.DESKTOP,
    overflow: 'hidden',

    [theme.breakpoints.down('md')]: {
        height: HERO_HEIGHT.TABLET,
    },
    [theme.breakpoints.down('sm')]: {
        height: HERO_HEIGHT.MOBILE,
    },
}));

const HeroImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
});

const TextOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center', // Center content horizontally
    padding: '0 10%',
    backgroundColor: 'rgba(10, 35, 66, 0.25)', // Very transparent navy overlay over entire image
    [theme.breakpoints.down('sm')]: {
        padding: '0 5%', // Less padding on mobile
    },
}));

// Text container - minimal or no background, just holds text
const TextBackdrop = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center', // Center text horizontally
    textAlign: 'center', // Center text content
    backgroundColor: 'transparent', // No background, overlay provides contrast
    padding: '10px 0',
    borderRadius: '8px',
    maxWidth: 'fit-content',
}));

interface StyledTypographyProps {
    $isvisible: boolean;
    $direction: 'left' | 'right';
    component?: React.ElementType;
}

const AnimatedTypography = styled(Typography, {
    shouldForwardProp: (prop) => !['$isvisible', '$direction'].includes(prop as string),
})<StyledTypographyProps>(({ $isvisible, $direction, theme }) => `
    opacity: ${$isvisible ? 1 : 0};
    transform: translateX(${$isvisible ? 0 : ($direction === 'right' ? 50 : -50)}px);
    transition: opacity 1s, transform 1s;
    
    /* Subtle text shadow for readability */
    text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
    
    color: #FFFFFF;
    font-weight: ${$direction === 'left' ? 'bold' : 'semibold'};
    line-height: ${$direction === 'right' ? 1.3 : 'inherit'};
    white-space: ${$direction === 'right' ? 'pre-line' : 'normal'};

    ${theme.breakpoints.down('sm')} {
        font-size: ${$direction === 'left' ? '1.75rem' : '1.125rem'};
    }

    ${theme.breakpoints.between('sm', 'md')} {
        font-size: ${$direction === 'left' ? '2rem' : '1.25rem'};
    }

    ${theme.breakpoints.between('md', 'lg')} {
        font-size: ${$direction === 'left' ? '2.5rem' : '1.5rem'};
    }

    ${theme.breakpoints.up('lg')} {
        font-size: ${$direction === 'left' ? '2.75rem' : '1.5rem'};
    }
`);

const OrangeDivider = styled('div')(({ theme }) => ({
    width: '300px',
    maxWidth: '300px',
    height: '3px',
    backgroundColor: theme.palette.orange.main,
    margin: '1px auto', // Center the divider
    transformOrigin: 'center', // Transform from center instead of left
    transform: 'scaleX(0)',
    transition: 'transform 1s ease',
    willChange: 'transform',
    '&.visible': {
        transform: 'scaleX(1)',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '200px',
        },
    },
}));

const HeroSection: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 500);
        return () => clearTimeout(timer);
    }, []);

    const handleGetQuote = () => {
        navigate('/quoter');
    };

    return (
        <HeroContainer>
            <picture>
                <source 
                    media="(max-width: 600px)" 
                    srcSet="/images/sugar-sue-mobile.webp" 
                    type="image/webp"
                    width="800"
                    height="257"
                />
                <source 
                    media="(min-width: 601px) and (max-width: 960px)" 
                    srcSet="/images/sugar-sue-tablet.webp" 
                    type="image/webp"
                    width="1200"
                    height="385"
                />
                <source 
                    media="(min-width: 961px)" 
                    srcSet="/images/sugar-sue-desktop.webp" 
                    type="image/webp"
                    width="1792"
                    height="576"
                />
                <HeroImage
                    src="/images/sugar-sue.png"
                    alt="Point Sails custom replacement sails for sailboats - Quality sailmaking since 1991"
                    width="1792"
                    height="576"
                    decoding="async"
                    {...({ fetchpriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
                />
            </picture>
            <TextOverlay>
                {/* Wrapper that shows navy box on mobile only */}
                <TextBackdrop>
                    <AnimatedTypography
                        variant="h2"
                        component="h1"
                        $isvisible={isVisible}
                        $direction="left"
                        sx={{ mb: 2 }}
                    >
                        <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>Quality Sails</Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>&nbsp;</Box>
                        <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>Fair Prices</Box>
                    </AnimatedTypography>
                    <OrangeDivider className={isVisible ? 'visible' : ''} />
                    <AnimatedTypography
                        variant="h1"
                        component="p"
                        $isvisible={isVisible}
                        $direction="right"
                    >
                        {'Sailmaking since 1991'}
                    </AnimatedTypography>
                    <AnimatedTypography
                        variant="body1"
                        $isvisible={isVisible}
                        $direction="right"
                        sx={{
                            mt: 1.5,
                            opacity: 0.9,
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                            fontWeight: 'bold'
                        }}
                    >
                        Trusted by San Diego sailors for decades — now available nationwide
                    </AnimatedTypography>

                    {/* Desktop CTA - Hidden on mobile */}
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            alignItems: 'center',
                            mt: 3,
                            opacity: isVisible ? 1 : 0,
                            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s',
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleGetQuote}
                            sx={{
                                backgroundColor: 'orange.main',
                                color: 'white',
                                fontSize: '1.125rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                px: 4,
                                py: 1.5,
                                borderRadius: '8px',
                                boxShadow: '0 4px 14px rgba(243, 112, 33, 0.4)',
                                '&:hover': {
                                    backgroundColor: 'orange.dark',
                                    boxShadow: '0 6px 20px rgba(243, 112, 33, 0.5)',
                                },
                            }}
                        >
                            Get Your Quote →
                        </Button>
                        <Typography
                            variant="body1"
                            sx={{
                                mt: 1.5,
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                                fontWeight: 'bold',
                                textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                            }}
                        >
                            Instant pricing for 5,000+ boats
                        </Typography>
                    </Box>
                </TextBackdrop>
            </TextOverlay>
        </HeroContainer>
    );
};

export default HeroSection;
